/**
 * Delivery Cockpit guided tour — persona resolution and version keys.
 */

export const DELIVERY_COCKPIT_TOUR_VERSION = 'v3';

export const DELIVERY_COCKPIT_TOUR_DISMISSED_KEY = 'synaptix_delivery_cockpit_tour_dismissed';

/** One-time auto-open per user+version. */
export const DELIVERY_COCKPIT_TOUR_AUTO_OPENED_KEY = 'synaptix_delivery_cockpit_tour_auto_opened';

/** Set by /getting-started before login; opens tour on next authenticated load. */
export const OPEN_TOUR_AFTER_LOGIN_KEY = 'synaptix_open_tour_after_login';

/** @deprecated Use OPEN_TOUR_AFTER_LOGIN_KEY */
export const OPEN_HELP_AFTER_LOGIN_KEY = OPEN_TOUR_AFTER_LOGIN_KEY;

/** Progress payload stored per user+persona. */
export const DELIVERY_COCKPIT_TOUR_PROGRESS_PREFIX = 'synaptix_tour_progress';

/**
 * @typedef {'pm'|'executive'|'admin'|'ic'|'approver'|'finance'|'client'} TourPersona
 */

/**
 * Map auth role to guided tour persona.
 *
 * @param {string|null|undefined} role - User role from AuthContext.
 * @param {{ is_superuser?: boolean }} [options]
 * @returns {TourPersona}
 */
export const resolveDeliveryCockpitTourPersona = (role, options = {}) => {
    if (options.is_superuser || role === 'admin') {
        return 'admin';
    }
    if (role === 'finance' || role === 'accountant') {
        return 'finance';
    }
    if (role === 'team_leader') {
        return 'approver';
    }
    if (role === 'employee' || role === 'engineer') {
        return 'ic';
    }
    if (role === 'director' || role === 'ceo' || role === 'cto' || role === 'cfo') {
        return 'executive';
    }
    if (role === 'client') {
        return 'client';
    }
    if (role === 'project_manager' || role === 'manager') {
        return 'pm';
    }
    return 'pm';
};

/**
 * Tour graph id for a persona.
 *
 * @param {TourPersona} persona
 * @returns {string}
 */
export const getTourIdForPersona = (persona) => {
    const map = {
        pm: 'pm-golden-path',
        executive: 'exec-delivery-path',
        admin: 'admin-full-path',
        ic: 'ic-timesheet-path',
        approver: 'approver-queue-path',
        finance: 'finance-billing-path',
        client: 'client-portal-path',
    };
    return map[persona] || 'pm-golden-path';
};

/**
 * Whether the tour should auto-open for this persona on login.
 *
 * @param {TourPersona} persona
 * @returns {boolean}
 */
export const shouldAutoOpenTourForPersona = (persona) => persona !== 'client';

/**
 * Human-readable persona label for the coach panel.
 *
 * @param {TourPersona} persona
 * @returns {string}
 */
export const getTourPersonaLabel = (persona) => {
    const labels = {
        pm: 'Project manager',
        executive: 'Executive',
        admin: 'Admin',
        ic: 'Engineer',
        approver: 'Approver',
        finance: 'Finance',
        client: 'Client',
    };
    return labels[persona] || 'Delivery Cockpit';
};
